import {
  LinearProgress,
  StandardTextFieldProps,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import { useDeepEqualMemo } from '@superdispatch/hooks';
import { identity } from 'es-toolkit/compat';
import {
  forwardRef,
  HTMLAttributes,
  ReactNode,
  UIEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { APIListQueryData } from 'shared/api/APIListQuery';
import { useSearchQueryText } from 'shared/helpers/ReactHelpers';
import { ColorDynamic } from '@superdispatch/ui';

const StyledTextField = styled(TextField)`
  &[data-has-placeholder-color='true'] .MuiInputBase-input::placeholder {
    color: ${ColorDynamic.Dark500};
    opacity: 1;
  }
`;

const StyledListbox = styled.ul`
  overflow-x: hidden;

  & .MuiAutocomplete-option {
    min-width: 0;
  }
`;

const OptionLabelText = styled.span`
  display: block;
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListboxComponent = forwardRef<
  HTMLUListElement,
  HTMLAttributes<HTMLUListElement>
>(({ children, ...props }, ref) => (
  <StyledListbox {...props} ref={ref}>
    {children}

    {props['aria-busy'] && (
      <li>
        <LinearProgress />
      </li>
    )}
  </StyledListbox>
));

ListboxComponent.displayName = 'ListboxComponent';

interface OptionLabelProps {
  label: string;
}

function OptionLabel({ label }: OptionLabelProps) {
  return <OptionLabelText title={label}>{label}</OptionLabelText>;
}

interface UseResourceFieldOptionsHookOptions {
  enabled?: boolean;
}

type UseResourceFieldOptionsHook<TOption> = (
  query: string | undefined,
  options?: UseResourceFieldOptionsHookOptions,
) => {
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  data?: null | APIListQueryData<TOption>;
};

type UseResourceFieldSelectedOptionHook<TOption> = (
  key: null | string | undefined,
) => {
  data?: null | TOption;
};

interface BaseResourceFieldProps<TOption> extends Pick<
  AutocompleteProps<TOption, false, boolean, false>,
  'id' | 'disabled' | 'noOptionsText' | 'forcePopupIcon' | 'disableClearable'
> {
  value?: null | string;
  TextFieldProps?: StandardTextFieldProps;
  onChange?: (value: string | undefined) => void;
  startAdornment?: ReactNode;
  hasPlaceholderColor?: boolean;
  disableUntilValueResolved?: boolean;
}

interface ResourceFieldProps<
  TOption,
  TSelectedOption,
> extends BaseResourceFieldProps<TOption> {
  groupBy?: (option: TOption) => string;
  getOptionKey: (option: TOption | TSelectedOption) => string;
  getOptionLabel: (option: TOption | TSelectedOption) => string;
  useOptions: UseResourceFieldOptionsHook<TOption | TSelectedOption>;
  useSelectedOption: UseResourceFieldSelectedOptionHook<TSelectedOption>;
}

export function ResourceField<TOption, TSelectedOption>({
  disabled: disabledProp,

  value,
  onChange,

  useOptions,
  useSelectedOption,
  groupBy: groupByProp,
  getOptionKey: getOptionKeyProp,
  getOptionLabel: getOptionLabelProp,

  TextFieldProps = {},
  startAdornment,
  hasPlaceholderColor = false,
  disableUntilValueResolved = true,
  ...props
}: ResourceFieldProps<TOption, TSelectedOption>) {
  const {
    current: { groupBy, getOptionKey, getOptionLabel },
  } = useRef({
    groupBy: groupByProp,
    getOptionKey: getOptionKeyProp,
    getOptionLabel: getOptionLabelProp,
  });

  const [searchText, searchQuery, setSearchText] = useSearchQueryText('');
  const [hasOpened, setHasOpened] = useState(false);

  const {
    data: options,
    hasNextPage: hasNextOptionsPage,
    fetchNextPage: fetchNextOptionsPage,
    isFetchingNextPage: isFetchingNextOptionsPage,
  } = useOptions(searchQuery, { enabled: hasOpened });
  const { data: selectedOption } = useSelectedOption(value);

  const [keys, optionsMap] = useDeepEqualMemo(() => {
    const nextKeys: string[] = [];
    const nextOptionsMap = new Map<
      string,
      undefined | TOption | TSelectedOption
    >();

    if (options) {
      for (const page of options.pages) {
        for (const option of page.data) {
          const optionKey = getOptionKey(option);

          nextKeys.push(optionKey);
          nextOptionsMap.set(optionKey, option);
        }
      }
    }

    if (selectedOption) {
      const selectedOptionKey = getOptionKey(selectedOption);

      // Add `selectedOption` as first if it's not included in the `options`.
      if (!nextOptionsMap.has(selectedOptionKey)) {
        nextKeys.unshift(selectedOptionKey);
        nextOptionsMap.set(selectedOptionKey, selectedOption);
      }
    }

    return [nextKeys, nextOptionsMap];
  }, [options, getOptionKey, selectedOption]);

  const getOptionKeyLabel = useCallback(
    (key: string) => {
      const option = optionsMap.get(key);

      if (option) {
        const label = getOptionLabel(option);

        if (label) {
          return label;
        }
      }

      return '';
    },
    [getOptionLabel, optionsMap],
  );

  const renderOptionKey = useCallback(
    (key: string) => <OptionLabel label={getOptionKeyLabel(key) || '…'} />,
    [getOptionKeyLabel],
  );

  const handleScroll = useCallback(
    ({
      currentTarget: { scrollTop, scrollHeight, clientHeight },
    }: UIEvent<HTMLUListElement>) => {
      if (!hasNextOptionsPage || isFetchingNextOptionsPage) return;

      const threshold = 32 * 5;
      const delta = scrollHeight - scrollTop - threshold;

      if (delta <= clientHeight) {
        fetchNextOptionsPage();
      }
    },
    [fetchNextOptionsPage, hasNextOptionsPage, isFetchingNextOptionsPage],
  );

  const disabled =
    disabledProp || (disableUntilValueResolved && !!value && !selectedOption);

  return (
    <Tooltip
      placement="top"
      open={searchText.length < 3 && searchText !== ''}
      title={searchText.length < 3 ? 'Type at least 3 characters' : ''}
    >
      <Autocomplete
        {...props}
        options={keys}
        multiple={false}
        autoComplete={true}
        autoHighlight={true}
        filterOptions={identity}
        disabled={disabled}
        loading={disabled || (hasOpened && !options)}
        value={selectedOption ? getOptionKey(selectedOption) : null}
        onChange={(_, nextValue) => {
          onChange?.(nextValue ?? undefined);
        }}
        onOpen={() => {
          setHasOpened(true);
        }}
        inputValue={searchText}
        onInputChange={(_, nextSearchText) => {
          if (nextSearchText) setHasOpened(true);
          setSearchText(nextSearchText);
        }}
        getOptionLabel={getOptionKeyLabel}
        renderOption={renderOptionKey}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            {...TextFieldProps}
            variant="outlined"
            data-has-placeholder-color={hasPlaceholderColor}
            InputProps={{
              ...params.InputProps,
              ...TextFieldProps.InputProps,
              startAdornment:
                startAdornment || TextFieldProps.InputProps?.startAdornment,
            }}
          />
        )}
        groupBy={
          !groupBy
            ? undefined
            : (key) => groupBy(optionsMap.get(key) as TOption)
        }
        ListboxComponent={ListboxComponent}
        ListboxProps={{
          onScroll: handleScroll,
          'aria-busy': isFetchingNextOptionsPage,
        }}
      />
    </Tooltip>
  );
}
